import { Combobox } from "@headlessui/react";
import { Place } from "./types";
import { ChangeEvent, Dispatch, useCallback, useEffect, useState } from "react";
import LocationAlt from "../../icons/LocationAlt";
import CurrentLocation from "../../icons/CurrentLocation";
import { useQuery } from "@tanstack/react-query";
import removeUSA from "../../../lib/fns/removeUSA";
import getLocationFromCoords from "../../../lib/api/getLocationFromCoordinates";
import { UserInfo } from ".";
import Spinner from "../../icons/Spinner";
import { toast } from "react-toastify";
import searchZipcodes from "../../../lib/api/searchZipcodes";
import getPlacesSearchRequest from "../../../lib/api/getPlacesSearchRequest";

interface LocationInputProps {
  selectedPlace: Place | null;
  setSelectedPlace: Dispatch<Place>;
  setUpdateUserInfo: Dispatch<UserInfo>
}

const LocationInput: React.FC<LocationInputProps> = ({ setSelectedPlace, setUpdateUserInfo }) => {


  const [query, setQuery] = useState('');
  const [selectedZipcode, setSelectedZipcode] = useState<{ description: string } | null>(null)
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null)

  const [isLoading, setIsLoading] = useState(false);

  const { data: location } = useQuery(['location-from-coordinates', coordinates], () => {
    if (coordinates) {
      return getLocationFromCoords(coordinates)
    }

    return null
  })


  const { data: places } = useQuery(['places', query], () => {
    if (query) {

      return searchZipcodes(query);
    }
    return []
  })
  const handleChange = useCallback(async (changeEvent: ChangeEvent<HTMLInputElement>) => {
    setQuery(changeEvent.target.value);
  }, [])


  const handleGeolocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates([longitude, latitude]);
          setIsLoading(false)
        },
        (error) => {
          toast.error(error.message);
          setIsLoading(false)
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');

    }
  }

  useEffect(() => {
    if (location) {
      setUpdateUserInfo(location)
      setSelectedPlace({
        place_id: location.location.place_id,
        description: location.zipcodeLocation
      })
    }
  }, [location])


  const getPlaceFromZipcode = useCallback(async (selectedZipcode: { description: string }) => {
    try {

      setIsLoading(true)
      const placeFromInput = await getPlacesSearchRequest(selectedZipcode.description).then(res => res.predictions)
      setSelectedPlace(placeFromInput[0])
    } catch {
      toast.error('Error fetching location details.')
    } finally {
      setIsLoading(false)

    }

  }, [selectedZipcode])

  useEffect(() => {
    if (selectedZipcode) {
      getPlaceFromZipcode(selectedZipcode)
    }
  }, [selectedZipcode])

  return <div className="relative w-full py-4">

    <Combobox as="div" className="w-full bg-white rounded-[10px]" value={selectedZipcode} onChange={setSelectedZipcode}>
      <div className="relative">

        <Combobox.Input placeholder="Zip or City, State" displayValue={(place: Place) => isLoading ? '' : removeUSA(place?.description)}
          onChange={handleChange} className="w-full h-full px-[50px] border-2 border-primary-dark py-4 rounded-[10px]" type="text" />
        <LocationAlt className={" absolute top-3 left-4 w-6 h-7 stroke-primary-dark fill-transparent stroke-1.5"} />
        {
          isLoading && <Spinner className="absolute top-3 left-16 w-7 h-7 border-profileButtons border-l-profileButtons/50" />
        }
        <button type="button" className="absolute right-0 border-black/20 flex items-center justify-center top-2 w-[50px] bottom-2 border-l-1">
          <CurrentLocation onClick={handleGeolocation} className={"w-6 h-7 fill-profileButtons stroke-1.5"} />
        </button>
      </div>
      {
        Boolean(query) &&

        <Combobox.Options className="py-4 text-left min-h-[150px]">
          {places?.map((place: Place) => (
            <Combobox.Option key={place.place_id} itemType="div" className="px-4 py-1 cursor-pointer hover:bg-profileButtons hover:text-white" value={place}>
              {removeUSA(place.description)}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      }
    </Combobox>

  </div>;
};

export default LocationInput;